import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useContent } from "@/context/ContentContext";

// ─── Shared geometric mark — simple 8-pointed star ────────────────────────────
const GeomBadge = ({ className = "w-16 h-16" }) => (
  <svg viewBox="0 0 100 100" className={`${className} transition-transform duration-700 group-hover:rotate-[22.5deg]`}>
    {/* outer 8-pointed star */}
    <path
      d="M50,12 L56.1,35.2 L76.9,23.1 L64.8,43.9 L88,50 L64.8,56.1 L76.9,76.9 L56.1,64.8 L50,88 L43.9,64.8 L23.1,76.9 L35.2,56.1 L12,50 L35.2,43.9 L23.1,23.1 L43.9,35.2 Z"
      fill="none" stroke="#C8A24A" strokeWidth="1.8"
    />
    {/* centre dot */}
    <circle cx="50" cy="50" r="3.5" fill="#C8A24A" />
  </svg>
);

// Large ambient background mark
const AmbientMark = ({ className }) => (
  <svg viewBox="0 0 100 100" className={className}>
    <path
      d="M50,12 L56.1,35.2 L76.9,23.1 L64.8,43.9 L88,50 L64.8,56.1 L76.9,76.9 L56.1,64.8 L50,88 L43.9,64.8 L23.1,76.9 L35.2,56.1 L12,50 L35.2,43.9 L23.1,23.1 L43.9,35.2 Z"
      fill="none" stroke="#C8A24A" strokeWidth="1"
    />
    <circle cx="50" cy="50" r="46" fill="none" stroke="#C8A24A" strokeWidth="0.5" opacity="0.5" />
    <circle cx="50" cy="50" r="2.5" fill="#C8A24A" />
  </svg>
);

// ─── Data ─────────────────────────────────────────────────────────────────────

const DEFAULT_PROJECTS = [
  { name: "Salah Companion", category: "Mobile App", desc: "Prayer times, qibla compass and adhan reminders — a daily companion trusted across timezones.", tags: ["iOS", "Android", "Geolocation"] },
  { name: "Madrasah Online", category: "Web Platform", desc: "A complete e-learning madrasah: Quran recitation classes, Arabic courses and teacher dashboards.", tags: ["E-learning", "Video", "Payments"] },
  { name: "Halal Finder", category: "Web + Mobile", desc: "Discover verified halal restaurants and markets nearby, with community reviews and certification info.", tags: ["Maps", "Directory", "Reviews"] },
  { name: "Zakat Pro", category: "Web App", desc: "Precise zakat calculation across assets with secure giving to vetted causes worldwide.", tags: ["Fintech", "Calculator", "Donations"] },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

// ─── Component ────────────────────────────────────────────────────────────────

export const Portfolio = () => {
  const { content } = useContent();
  const p = content?.portfolio;

  const projects = (p?.projects?.length ? p.projects : DEFAULT_PROJECTS).map((proj, i) => ({
    ...proj,
    slug: (proj.name || `project-${i}`).toLowerCase().replace(/\s+/g, "-"),
  }));

  return (
    <section id="work" data-testid="portfolio-section" className="relative py-24 lg:py-32 bg-[#0E2418] overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.06] mix-blend-screen pointer-events-none"
        style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/34256958/pexels-photo-34256958.jpeg?auto=compress&w=800)",
          backgroundSize: "420px",
        }}
      />

      <div className="absolute -right-44 -top-44 opacity-[0.09] pointer-events-none" aria-hidden="true">
        <AmbientMark className="w-[560px] h-[560px]" />
      </div>
      <div className="absolute -left-32 -bottom-32 opacity-[0.05] pointer-events-none" aria-hidden="true">
        <AmbientMark className="w-[340px] h-[340px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="mb-14 flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <p className="uppercase tracking-[0.35em] text-xs text-[#C8A24A] mb-6 flex items-center gap-4">
              <span className="w-12 h-px bg-[#C8A24A]/60" />
              {p?.eyebrow || "Selected work"}
            </p>
            <h2
              data-testid="portfolio-heading"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#F5F0E6]"
            >
              {p?.heading || "Crafted with"}{" "}
              <em className="text-[#C8A24A]">{p?.heading_accent || "ihsan"}</em>
            </h2>
          </div>
          <p className="hidden lg:block text-xs uppercase tracking-[0.25em] text-[#A3B8AD]/70 pb-2">
            Hover a project
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          transition={{ staggerChildren: 0.1 }}
          className="border-b border-[#C8A24A]/20"
        >
          {projects.map((proj, i) => (
            <motion.div
              key={proj.slug}
              variants={fadeUp}
              data-testid={`portfolio-card-${proj.slug}`}
              className="group border-t border-[#C8A24A]/20 cursor-pointer"
            >
              <div className="grid grid-cols-[44px_1fr_auto] lg:grid-cols-[90px_1fr_auto] items-center gap-4 lg:gap-8 py-7 lg:py-9">
                <span className="font-heading text-lg lg:text-xl text-[#C8A24A]/70">0{i + 1}</span>
                <h3 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#F5F0E6] transition-all duration-500 group-hover:text-[#E3C97A] lg:group-hover:translate-x-3">
                  {proj.name}
                </h3>
                <div className="flex items-center gap-5">
                  <span className="hidden sm:block uppercase tracking-[0.25em] text-[11px] text-[#A3B8AD]">
                    {proj.category}
                  </span>
                  <ArrowUpRight
                    size={22}
                    className="text-[#C8A24A] opacity-0 -translate-x-2 translate-y-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all duration-500"
                  />
                </div>
              </div>

              {/* hover-expanding reveal */}
              <div className="grid grid-rows-[1fr] lg:grid-rows-[0fr] lg:group-hover:grid-rows-[1fr] transition-[grid-template-rows] duration-500 ease-out">
                <div className="overflow-hidden">
                  <div className="pb-9 lg:pl-[122px] flex flex-col sm:flex-row sm:items-center gap-6 lg:gap-12">
                    <GeomBadge className="w-16 h-16 shrink-0" />
                    <div>
                      <p className="text-[#A3B8AD] font-light text-sm lg:text-base leading-relaxed max-w-xl">
                        {proj.desc}
                      </p>
                      {proj.tags?.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {proj.tags.map((t) => (
                            <span
                              key={t}
                              className="text-[11px] uppercase tracking-wider border border-[#C8A24A]/25 text-[#C8A24A] px-3 py-1"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
